// Plaid REST client — talks to our Cloudflare Worker proxy (NOT to
// api.plaid.com directly; Plaid doesn't ship CORS headers, verified
// 2026-05-15). The Worker validates the endpoint shape and forwards
// the body verbatim. The user's client_id + secret travel inside the
// request body per Plaid's REST schema, exactly as the desktop's
// src-tauri/src/plaid.rs sends them.
//
// Sibling to simplefin_client.ts. Mirrors the same shape: thin typed
// wrappers, no caching, no retry except the documented warm-up loop
// on /transactions/sync for fresh Items (deferred to C4).
//
// Plaid API reference: https://plaid.com/docs/api/

// ── proxy URL ──────────────────────────────────────────────────────
//
// Pinned to the Worker deployed by `.github/workflows/deploy-plaid-proxy.yml`.
// First deploy ran 2026-05-15 and Cloudflare assigned this URL based on
// the account's workers-subdomain. Redeploys of the Worker preserve the
// URL — it's stable per (account, worker name).
//
// Override at build time with VITE_PLAID_PROXY_URL if forking the
// project to a different Cloudflare account.

const PROXY_URL: string =
  (import.meta.env?.VITE_PLAID_PROXY_URL as string | undefined) ??
  "https://pf-plaid-proxy.zashraf1337.workers.dev";

export type PlaidEnv = "production" | "sandbox";

// ── shared types (only the fields the web app touches) ─────────────

export interface PlaidCredentials {
  client_id: string;
  secret: string;
  env: PlaidEnv;
  /** ISO 3166-1 alpha-2 country codes the user's Plaid app should
   *  surface banks for. Optional in the type for backwards compat —
   *  legacy creds saved before C23 don't carry this field. Callers
   *  fall back to ["US", "CA"] (the desktop's default). Plaid's
   *  current Transactions-supported list (production): US, CA, GB,
   *  IE, ES, FR, NL, DE, IT, PL, DK, NO, SE, BE, EE, LT, LV. */
  country_codes?: string[];
}

/** Plaid-supported country codes for the Transactions product
 *  (production), with display labels. Source of truth for the
 *  credentials-modal multi-select. */
export const PLAID_TRANSACTIONS_COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "ES", name: "Spain" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "DE", name: "Germany" },
  { code: "IT", name: "Italy" },
  { code: "PL", name: "Poland" },
  { code: "BE", name: "Belgium" },
  { code: "DK", name: "Denmark" },
  { code: "NO", name: "Norway" },
  { code: "SE", name: "Sweden" },
  { code: "EE", name: "Estonia" },
  { code: "LT", name: "Lithuania" },
  { code: "LV", name: "Latvia" },
];

export const DEFAULT_PLAID_COUNTRIES: ReadonlyArray<string> = ["US", "CA"];

export interface LinkTokenCreateRequest {
  client_id: string;
  secret: string;
  user: { client_user_id: string };
  client_name: string;
  /** Empty / omitted in update mode (where access_token is set). */
  products?: string[];
  language: string;
  country_codes: string[];
  /** redirect_uri — required for OAuth banks like Chase / BofA. Must
   *  exactly match a URL registered in the user's Plaid dashboard. */
  redirect_uri?: string;
  /** Per-product config. We pass `days_requested` for transactions so
   *  Plaid pre-pulls the full 24-month historical window once at link
   *  time. Plaid's docs note that an Item is linked with N days as a
   *  hard ceiling — any later /transactions/sync (even after a cursor
   *  reset) will never exceed N. Setting the production max once
   *  unlocks "Reset & re-pull" to return the full 24 months. */
  transactions?: { days_requested?: number };
  /** Update-mode: pass an existing access_token to refresh an Item's
   *  bank credentials without creating a new Item. Plaid Link then
   *  prompts the user to re-authenticate (typically after they
   *  changed their bank password). When this is set, `products` MUST
   *  be omitted/empty — Plaid rejects the combination otherwise. */
  access_token?: string;
}

export interface LinkTokenCreateResponse {
  link_token: string;
  expiration: string;
  request_id: string;
}

export interface ExchangePublicTokenResponse {
  access_token: string;
  item_id: string;
  request_id: string;
}

export interface TransactionsSyncRequest {
  client_id: string;
  secret: string;
  access_token: string;
  cursor?: string;
  count?: number;
}

export interface PlaidApiAccount {
  account_id: string;
  name?: string;
  mask?: string;
  subtype?: string;
  type?: string;
}

export interface PlaidApiTransaction {
  transaction_id: string;
  account_id: string;
  date: string;
  amount: number;
  name?: string;
  merchant_name?: string;
  iso_currency_code?: string;
  pending?: boolean;
}

export interface TransactionsSyncResponse {
  accounts: PlaidApiAccount[];
  added: PlaidApiTransaction[];
  modified: PlaidApiTransaction[];
  removed: Array<{ transaction_id: string; account_id?: string }>;
  next_cursor: string;
  has_more: boolean;
  request_id: string;
}

export interface InstitutionsGetByIdResponse {
  institution: {
    institution_id: string;
    name: string;
    country_codes: string[];
    products?: string[];
  };
  request_id: string;
}

export interface ItemRemoveResponse {
  request_id: string;
}

// ── error shape ────────────────────────────────────────────────────

export interface PlaidErrorBody {
  error_type?: string;
  error_code?: string;
  error_message?: string;
  display_message?: string | null;
  request_id?: string;
}

export class PlaidApiError extends Error {
  readonly status: number;
  readonly body: PlaidErrorBody;
  constructor(status: number, body: PlaidErrorBody) {
    const summary =
      body.error_message ??
      body.display_message ??
      `${body.error_type ?? "PLAID_ERROR"} / ${body.error_code ?? "(no code)"}`;
    super(summary);
    this.name = "PlaidApiError";
    this.status = status;
    this.body = body;
  }
}

// ── low-level transport ────────────────────────────────────────────

/** POST a Plaid request body through our Worker proxy. Throws
 *  PlaidApiError on non-2xx so callers can pattern-match on
 *  error_code (e.g. INVALID_API_KEYS, ITEM_LOGIN_REQUIRED). */
export async function plaidCall<T>(
  env: PlaidEnv,
  endpoint: string,
  body: object,
): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(PROXY_URL, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-plaid-env": env,
        "x-plaid-endpoint": endpoint,
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    // Network / DNS failure before we ever reach the proxy.
    throw new PlaidApiError(0, {
      error_type: "PROXY_UNREACHABLE",
      error_code: "PROXY_UNREACHABLE",
      error_message: err instanceof Error ? err.message : String(err),
    });
  }

  // 4xx / 5xx — try to parse Plaid's standard error envelope. If the
  // body isn't JSON (Worker 502, etc.) wrap it in our error shape.
  if (!resp.ok) {
    let body: PlaidErrorBody;
    try {
      body = (await resp.json()) as PlaidErrorBody;
    } catch {
      body = {
        error_type: "PLAID_HTTP_ERROR",
        error_code: `HTTP_${resp.status}`,
        error_message: `HTTP ${resp.status} from Plaid proxy`,
      };
    }
    throw new PlaidApiError(resp.status, body);
  }
  return (await resp.json()) as T;
}

// ── typed wrappers ─────────────────────────────────────────────────

/** Create a one-time link_token to hand to Plaid Link. The
 *  client_user_id is an opaque stable identifier for the user — we use
 *  the origin (per-browser, per-machine) since each web-app install is
 *  effectively its own user from Plaid's perspective.
 *
 *  Two modes, decided by whether `accessToken` is set:
 *  - Connect mode (no accessToken): the standard "link a new bank"
 *    flow. Includes `products: ["transactions"]` and the 730-day
 *    history window.
 *  - Update mode (accessToken set): re-authenticate an existing Item
 *    after the user changed their bank password. `products` must be
 *    omitted; Plaid rejects the combination otherwise. */
export function linkTokenCreate(
  creds: PlaidCredentials,
  args: { redirectUri?: string; clientUserId?: string; accessToken?: string },
): Promise<LinkTokenCreateResponse> {
  const body: LinkTokenCreateRequest = {
    client_id: creds.client_id,
    secret: creds.secret,
    user: { client_user_id: args.clientUserId ?? defaultClientUserId() },
    client_name: "Personal Finance — Web",
    language: "en",
    country_codes: [...(creds.country_codes ?? DEFAULT_PLAID_COUNTRIES)],
  };
  if (args.accessToken) {
    // Update mode — Plaid uses the access_token to look up the
    // existing Item and re-authenticate it. products + transactions
    // config are forbidden in this branch.
    body.access_token = args.accessToken;
  } else {
    body.products = ["transactions"];
    // Plaid's production max. Matches the desktop's create_link_token
    // body — once an Item is linked with N days, /transactions/sync
    // will never return more than N days for that Item even after a
    // cursor reset. Setting this once at link time makes the
    // "Reset & re-pull" action able to return the full 24 months.
    body.transactions = { days_requested: 730 };
  }
  if (args.redirectUri) body.redirect_uri = args.redirectUri;
  return plaidCall<LinkTokenCreateResponse>(creds.env, "/link/token/create", body);
}

/** Exchange the public_token Plaid Link delivers via onSuccess for a
 *  long-lived access_token. The access_token is the secret used for
 *  every subsequent /transactions/sync call — store it in the vault. */
export function exchangePublicToken(
  creds: PlaidCredentials,
  publicToken: string,
): Promise<ExchangePublicTokenResponse> {
  return plaidCall<ExchangePublicTokenResponse>(
    creds.env,
    "/item/public_token/exchange",
    {
      client_id: creds.client_id,
      secret: creds.secret,
      public_token: publicToken,
    },
  );
}

/** One page of cursor-paginated /transactions/sync. Caller loops while
 *  has_more is true, threading next_cursor back into subsequent calls.
 *  See C4 — the orchestrator handles the 90 s warm-up retry for newly-
 *  linked Items separately. */
export function transactionsSync(
  creds: PlaidCredentials,
  accessToken: string,
  cursor: string = "",
  count: number = 500,
): Promise<TransactionsSyncResponse> {
  return plaidCall<TransactionsSyncResponse>(creds.env, "/transactions/sync", {
    client_id: creds.client_id,
    secret: creds.secret,
    access_token: accessToken,
    cursor,
    count,
  });
}

/** Disconnect an Item. After this, the access_token is dead — calls
 *  return ITEM_NOT_FOUND. Caller should also wipe the vault entry. */
export function itemRemove(
  creds: PlaidCredentials,
  accessToken: string,
): Promise<ItemRemoveResponse> {
  return plaidCall<ItemRemoveResponse>(creds.env, "/item/remove", {
    client_id: creds.client_id,
    secret: creds.secret,
    access_token: accessToken,
  });
}

/** Look up an institution's display name by id. Used as a fallback —
 *  Plaid Link's onSuccess already passes the institution name through
 *  metadata, so this is rarely needed. */
export function institutionsGetById(
  creds: PlaidCredentials,
  institutionId: string,
): Promise<InstitutionsGetByIdResponse> {
  return plaidCall<InstitutionsGetByIdResponse>(creds.env, "/institutions/get_by_id", {
    client_id: creds.client_id,
    secret: creds.secret,
    institution_id: institutionId,
    country_codes: [...(creds.country_codes ?? DEFAULT_PLAID_COUNTRIES)],
  });
}

// ── helpers ────────────────────────────────────────────────────────

/** Opaque per-origin user id Plaid requires for /link/token/create.
 *  Stable across page loads (same origin → same value) but doesn't
 *  identify the user beyond that. */
function defaultClientUserId(): string {
  // origin is fine for desktop browsers; jsdom-less tests get a stub.
  const origin =
    typeof globalThis.location !== "undefined" && globalThis.location?.origin
      ? globalThis.location.origin
      : "pf-web-app";
  return `pf-web:${origin}`;
}

// Test seam — lets tests swap the proxy URL without rebuilding the
// module. The real PROXY_URL stays constant in production.
export const _testing = {
  proxyUrl(): string {
    return PROXY_URL;
  },
};
